import { type FC, type ReactElement, useMemo } from 'react';
import { RowData } from '@tanstack/react-table';
import { CellProps } from './types';
import { QIcon } from '../../../QIcons';
import { getMetaField, getMetaFieldMaybe } from './meta';
import { QBox, QStack } from '../../../QLayouts';

const Wrapper = <TData extends RowData, TValue>(
  Component: (props: CellProps<TData, TValue>) => ReactElement,
  props: CellProps<TData, TValue>,
): ReactElement => {
  const prepend = getMetaFieldMaybe(props.column, 'prepend');
  const append = getMetaFieldMaybe(props.column, 'append');
  const isLoadingCallback = getMetaField(props.column, 'isLoading');

  const isLoading = useMemo(
    () => isLoadingCallback(props.row.original, props.row.index),
    [isLoadingCallback, props.row.original],
  );

  const prependedValue = useMemo(
    () =>
      prepend ? (
        <QBox as="span">{prepend(props.row.original, props.row.index)}</QBox>
      ) : undefined,
    [prepend, props.row.original, props.row.index],
  );

  const appendedValue = useMemo(
    () =>
      append ? (
        <QBox as="span">{append(props.row.original, props.row.index)}</QBox>
      ) : undefined,
    [append, props.row.original, props.row.index],
  );

  if (isLoading) {
    return <QIcon iconName="EllipsisAnimated" />;
  }

  if (prependedValue || appendedValue) {
    return (
      <QStack direction="row" spacing="1" alignItems="center">
        {prependedValue}
        {<Component {...props} />}
        {appendedValue}
      </QStack>
    );
  }

  return <Component {...props} />;
};

Wrapper.displayName = 'CommonCellWrapper';

export const makeCellWrapper = <TData extends RowData, TValue>(
  Component: (props: CellProps<TData, TValue>) => ReactElement,
): FC<CellProps<TData, TValue>> => {
  return (props: CellProps<TData, TValue>) => Wrapper(Component, props);
};
